import { forwardRef, useCallback, useEffect, useId, useRef } from 'react';
import cx from 'classnames';
import './Checkbox.css';

/**
 * QHS Checkbox v2
 * ---------------
 *   <Checkbox checked={v} onChange={(e) => ...}>Remember me</Checkbox>
 *
 * `indeterminate` draws a dash instead of the tick — the "some of them"
 * state of a select-all box in a table header. It is a DOM property, not
 * an attribute, so it is set on the input after render.
 */
const Checkbox = forwardRef(function Checkbox(
  { label, description, className, id, children, indeterminate = false, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const inputRef = useRef(null);

  const setRefs = useCallback(
    node => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label htmlFor={inputId} className={cx('qhs-ctrl qhs-ctrl--checkbox', className)}>
      <input ref={setRefs} id={inputId} type="checkbox" className="qhs-ctrl__input" {...rest} />
      <span className="qhs-ctrl__box" aria-hidden="true">
        <svg className="qhs-ctrl__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg className="qhs-ctrl__dash" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <line x1="6" y1="12" x2="18" y2="12" />
        </svg>
      </span>
      {(label || children || description) && (
        <span className="qhs-ctrl__text">
          <span className="qhs-ctrl__label">{label || children}</span>
          {description && <span className="qhs-ctrl__desc">{description}</span>}
        </span>
      )}
    </label>
  );
});

export default Checkbox;
